import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchStore } from '@renderer/app/state/search-store';
import { Icon } from '@renderer/shared/components/icons';
import { TextField } from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';

export function GlobalSearchModal(): JSX.Element | null {
  const navigate = useNavigate();
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const query = useSearchStore((state) => state.query);
  const { activeIndex, close, error, isOpen, loadResults, moveActiveIndex, results, setActiveIndex, setQuery, status } = useSearchStore();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    void loadResults(query);
  }, [isOpen, loadResults, query]);

  if (!isOpen) {
    return null;
  }

  const activeResult = activeIndex >= 0 ? results[activeIndex] ?? null : null;

  function activateResult(): void {
    if (!activeResult) {
      return;
    }

    close();
    void navigate(activeResult.route);
  }

  return (
    <div
      className="av-search-modal__overlay"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          close();
        }
      }}
    >
      <div
        aria-labelledby="global-search-title"
        aria-modal="true"
        className="av-search-modal"
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="av-search-modal__header">
          <h2 id="global-search-title">{t('search.title')}</h2>
          <button className="av-search-modal__close" type="button" onClick={close}>
            x
          </button>
        </div>

        <div className="av-search-modal__input-wrap">
          <Icon name="search" />
          <TextField
            ref={inputRef}
            aria-label={t('search.title')}
            className="av-search-modal__input"
            unstyled
            value={query}
            onValueChange={setQuery}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                moveActiveIndex(1);
              }

              if (event.key === 'ArrowUp') {
                event.preventDefault();
                moveActiveIndex(-1);
              }

              if (event.key === 'Enter') {
                event.preventDefault();
                activateResult();
              }

              if (event.key === 'Escape') {
                event.preventDefault();
                close();
              }
            }}
            placeholder={t('search.inputPlaceholder')}
          />
        </div>

        <div className="av-search-modal__results">
          {status === 'loading' && results.length === 0 ? (
            <div className="av-search-modal__state">{t('search.loading')}</div>
          ) : null}
          {status === 'error' ? <div className="av-search-modal__state">{error}</div> : null}
          {status !== 'loading' && status !== 'error' && results.length === 0 ? (
            <div className="av-search-modal__state">
              <strong>{t('search.emptyTitle')}</strong>
              <p>{t('search.emptyDescription')}</p>
            </div>
          ) : null}

          {results.map((result, index) => (
            <button
              key={result.id}
              className={['av-search-result', index === activeIndex ? 'is-active' : ''].filter(Boolean).join(' ')}
              type="button"
              onClick={() => {
                setActiveIndex(index);
                close();
                void navigate(result.route);
              }}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <div className="av-search-result__icon">
                <Icon name={result.icon === 'leads' ? 'customers' : result.icon} />
              </div>
              <div className="av-search-result__copy">
                <strong>{result.title}</strong>
                {result.subtitle ? <span>{result.subtitle}</span> : null}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
